import './styles/index.css';
import { api } from './utils/api.ts';
import { consoleVersion, erudaConsole, reportWebVitals } from './utils/dev.ts';
import i18n from './utils/i18n.ts';
import { queryClient } from './utils/query-client.ts';
import { sentryOptions, whenIdle } from './utils/sentry-options.ts';
import { buildWebVitalsBeacon, elementLabel, isWebVitalsChunkError } from './utils/web-vitals-beacon.ts';
import App from '@/client/app.tsx';
import { ACTIVITY_DISTRIBUTOR_ADDRESS, ACTIVITY_ENROLLMENT_ENABLED, IS_DEBUG, SENTRY_DSN } from '@/client/confs/app.ts';
import { bootPrefetchFor, runBootPrefetch } from '@/client/confs/boot-prefetch';
import { preloadNamespaces } from '@/client/confs/i18n-preload';
import { prefetchRoute } from '@/client/confs/page-loaders';
import { shortMarketSource } from '@/client/pages/btc-5m/use-rounds-feed';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import 'virtual:uno.css';

// Deploys replace hashed chunks in place, so a session spanning a deploy 404s
// on lazy imports; reload once to pick up the new index.html (no-store). The
// 30s sessionStorage guard prevents reload loops if the reload doesn't fix it.
window.addEventListener('vite:preloadError', event => {
  // The RUM chunk is optional: reportWebVitals swallows its load error.
  if (isWebVitalsChunkError(event.payload)) return;
  const KEY = 'chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 30_000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

/**
 * Initialize application
 */
const initApp = async () => {
  // Route-aware warmup BEFORE the i18n await: the route chunk and the page's
  // first queries start now, in parallel with the locale files, instead of one
  // round trip each after the first render. Same loaders and query keys as the
  // pages (confs/boot-prefetch.ts), so the mounted page dedups on them.
  try {
    runBootPrefetch(
      bootPrefetchFor(window.location.pathname, {
        api,
        shortMarketSource,
        // /rewards is registered under this same condition in confs/routes.tsx.
        rewardsRoute: ACTIVITY_ENROLLMENT_ENABLED || !!ACTIVITY_DISTRIBUTOR_ADDRESS,
        // The settle rule banner is not mounted (5-minute settlement went back to the 1-minute kline on 10/8),
        // so nothing warms rounds.json for it.
        ruleBanner: false,
      }),
      { prefetchRoute, queryClient }
    );
  } catch {
    // A warmup problem never blocks the first render.
  }

  // Load every namespace for the active language BEFORE the first render.
  // react-i18next runs with Suspense on: rendering first and letting the 8
  // locale files trickle in made each arrival unmount/remount subtrees --
  // measured as the dominant CLS source on mobile (repeated ±131px flaps).
  // The fetch is ~30KB gz in parallel; the race caps the cost so a broken
  // locale fetch degrades to the old behavior instead of blanking the app.
  await Promise.race([
    i18n.loadNamespaces(preloadNamespaces(import.meta.env)).catch(() => {}),
    new Promise(resolve => setTimeout(resolve, 2500)),
  ]);

  // Render the app
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );

  // Initialize other services
  // add sentry (lazy-loaded to reduce initial bundle; imported when idle to stay out of the LCP path)
  if (SENTRY_DSN) {
    whenIdle(() => {
      import('@sentry/react').then(({ init, browserTracingIntegration, replayIntegration }) => {
        init({
          ...sentryOptions({
            dsn: SENTRY_DSN,
            siteUrl: import.meta.env.VITE_SITE_URL,
            apiUrl: import.meta.env.VITE_API_URL,
          }),
          integrations: [browserTracingIntegration(), replayIntegration()],
        });
      });
    });
  }
  if (IS_DEBUG) {
    erudaConsole();
  }
  // RUM: real-user Core Web Vitals beaconed to a same-origin 204 endpoint and
  // mined from nginx access logs (query-string payload on purpose — no
  // backend, no body logging). Route prefix only: event/market ids would
  // explode log cardinality.
  reportWebVitals(
    metric => {
      if (IS_DEBUG) console.log(metric.name, ':', metric.value);
      const routePrefix = `/${window.location.pathname.split('/')[1] || ''}`;
      const nav = navigator as Navigator & { deviceMemory?: number };
      const q = buildWebVitalsBeacon(metric, {
        ua: nav.userAgent,
        hardwareConcurrency: nav.hardwareConcurrency,
        deviceMemory: nav.deviceMemory,
        routePrefix,
      });
      navigator.sendBeacon?.(`/wv?${q}`);
    },
    // Low-cardinality target label (data-testid or tag), resolved while the
    // element is still attached
    { generateTarget: elementLabel }
  );
  consoleVersion(IS_DEBUG ? '1.0.0' : '1.0.0', i18n.t('title', { fallbackLng: 'en' })); // TODO version
};

// Initialize the application
initApp();
